/**
 * ThryvConnectBar — floating contact bar (Book / Chat / Call / Support).
 *
 * A fixed bar at the bottom of every page giving visitors the four contact
 * paths (web-dev request, Patricia Rollins 2026-07-29; design reference:
 * scorpion.co floating widget):
 *
 *   Book    → Chili Piper hosted scheduler (panel iframe, Config.bookingUrl)
 *   Chat    → Chili Piper chat widget (opened by CP's Custom Trigger on the
 *             button; visibility synced in EventHandlers.watchChatFrame)
 *   Call    → phone modal (per-site number, from the template config)
 *   Support → Intercom messenger (window.Intercom('show'))
 *
 * Architecture (namespace objects, ES modules — nothing is exposed on
 * window; the Alpine registry is the only public surface):
 *
 *   index.js         entry: Alpine registration + guarded Alpine boot
 *   Component.js     the Alpine data() factory + component wiring
 *   Config.js        Chili Piper production values (booking URL, chat embed)
 *   Chat.js          CP chat widget: bootstrap, state sync, widget events
 *   Book.js          CP hosted scheduler: iframe messages, spinner sync
 *   Support.js       Intercom: bar repositioning + supportOpen sync
 *   Helpers.js       generic plumbing (debug log, analytics dispatch)
 *   Controllers.js   domain utils (phone formatting, availability checks)
 *   Events.js        Events.all() → handler-name map bound in the template
 *   EventHandlers.js UI handlers (delegate CP plumbing to Chat/Book)
 *
 * Template usage (template-parts/thryv-connect-bar/main.php):
 *
 *   <div class="thryv-connect-bar" x-data='ThryvConnectBar(<?php echo json_encode($config) ?>)'>
 *     ...
 *     <button @click="evt.bookClick">Book</button>
 *   </div>
 */

import Alpine from 'alpinejs';

import Component from './Component.js';

/**
 * Register the named Alpine data component. Listening on alpine:init means
 * registration happens against WHICHEVER Alpine instance starts on the page
 * — ours (boot() below) or another bundle's (the DS2 scheduler plugin ships
 * its own). If a foreign Alpine already started before this bundle loaded,
 * register directly as a best effort (elements already scanned by then
 * cannot be recovered — keep this bundle enqueued normally, not delayed).
 */
function register() {

  window.Alpine.data('ThryvConnectBar', Component.data);

}

document.addEventListener('alpine:init', register);

if (window.Alpine) {

  register();

}

/**
 * Boot Alpine — deferred to DOMContentLoaded so registration wiring above is
 * in place before any x-data evaluates. If another bundle already owns
 * Alpine on this page, starting a second instance would double-initialize
 * every component — skip; register() already hooked into theirs.
 */
function boot() {

  if (window.Alpine) {

    return;

  }

  window.Alpine = Alpine;

  Alpine.start();

}

if (document.readyState === 'loading') {

  document.addEventListener('DOMContentLoaded', boot);

} else {

  boot();

}
